import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faMapMarkerAlt, faUsers } from '@fortawesome/free-solid-svg-icons/index';
import DatePicker from 'react-datepicker';
import TimePicker from 'rc-time-picker';
import Transactions from './Transactions';
import { getNowInTimeZone, useTimezoneForDate } from '../../../../helpers/timezones';
import { getTimeInfoUi, getJobTimeDurationUi, isJobSaved, resolveDateTime, shouldHoldStartTime } from '../../../../helpers/jobs';
import { getCentPrice } from '../../../../helpers/general';
import { dayDateFormat, timeFormatShort, timeFormatUi } from '../../../../constants/date';
import { alertMessages } from '../../../../constants/text';
import Validator from '../../../common/forms/Validator';
import ErrorMessage from '../../../common/forms/ErrorMessage';
import ActionsGroup from '../../../common/ui/ItemActions';
import Loader from '../../../common/ui/Loader';
import AddLocationModal from '../../createJob/createJob/LocationScheduleStep/AddLocationModal';

export default class GeneralInfo extends Validator {
  static propTypes = {
    jobDetails: PropTypes.shape({
      transactions: PropTypes.array,
      status: PropTypes.string.isRequired,
      is_approved: PropTypes.bool.isRequired,
      start_date: PropTypes.object.isRequired,
      end_date: PropTypes.object.isRequired,
      time_zone_id: PropTypes.string.isRequired,
      hours: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired // in case when field is empty it will be equal to empty string
      ]),
      quantity_of_workers: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired // in case when field is empty it will be equal to empty string
      ]),
      address: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
      address_coordinates: PropTypes.string.isRequired,
      location_name: PropTypes.string.isRequired,
      job_location_type_id: PropTypes.number.isRequired,
      event_id: PropTypes.number,
      event: PropTypes.object.isRequired,
      worker_pay_rate: PropTypes.number.isRequired,
      job_cost_rate: PropTypes.number.isRequired
    }),
    jobRates: PropTypes.shape({
      worker_pay_rate: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired // in case when field is empty it will be equal to empty string
      ]),
      job_cost_rate: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired // in case when field is empty it will be equal to empty string
      ])
    }),
    showEditIcons: PropTypes.bool.isRequired,
    locationsList: PropTypes.array.isRequired,
    companyLocations: PropTypes.array.isRequired,
    getCompanyLocations: PropTypes.func.isRequired,
    addUserLocation: PropTypes.func.isRequired,
    getLocationsListAction: PropTypes.func.isRequired,
    editJob: PropTypes.func.isRequired,
    getEventsList: PropTypes.func.isRequired,
    getCostPayRateByData: PropTypes.func.isRequired,
    updateJobRates: PropTypes.func.isRequired,
    UIShowAlert: PropTypes.func.isRequired,
    calculateEstCost: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    const { start_date, end_date, hours, quantity_of_workers } = props.jobDetails;
    this.initialJobDetails = {
      startDate: start_date,
      startTime: start_date,
      endTime: end_date,
      hours,
      quantity_of_workers
    };
    this.state = {
      isLoading: false,
      isEditing: false,
      ...this.initialJobDetails,
      selectedLocation: null,
      locationModalIsOpen: false
    };
  }

  validationRules = {
    startDate: { required: true },
    startTime: { required: true },
    endTime: { required: true },
    hours: { minJobHours: true, maxJobHours: true },
    quantity_of_workers: { required: true },
    address_coordinates: { required: true },
    worker_pay_rate: { required: true },
    job_cost_rate: { required: true }
  };

  componentDidMount () {
    const { getLocationsListAction, getCompanyLocations, jobDetails: { company_id } } = this.props;
    getLocationsListAction();
    getCompanyLocations(company_id).then(() => {
      const { location_name, address, state, city, zip, address_coordinates, job_location_type_id, time_zone_id } = this.props.jobDetails;
      const locationFromList = this.props.companyLocations.find(location => location.coordinates === address_coordinates);
      const locationId = locationFromList ? locationFromList.id : Date.now(); // temporary id, only for redux store
      const locationData = {
        location_name,
        address,
        state,
        city,
        zip,
        coordinates: address_coordinates,
        job_location_type_id,
        time_zone_id,
        id: locationId
      };
      const selectedLocation = {
        label: `${location_name}, ${address}`,
        value: locationId,
        data: locationData
      };
      this.initialJobDetails.selectedLocation = selectedLocation;

      if (locationFromList) {
        this.setState({ selectedLocation });
      } else {
        this.props.addUserLocation(locationData).then(() => this.setState({ selectedLocation }));
      }
    });
  }

  startLoading = () => {
    this.setState({ isLoading: true });
  };

  stopLoading = () => {
    this.setState({ isLoading: false });
  };

  startEditing = () => {
    this.setState({ isEditing: true });
  };

  stopEditing = () => {
    this.setState({ isEditing: false });
  };

  cancelEditing = () => {
    this.setState({
      ...this.initialJobDetails
    });
    const { job_cost_rate, worker_pay_rate } = this.props.jobDetails;
    this.props.updateJobRates({ job_cost_rate, worker_pay_rate });
    this._resetErrors();
    this.stopEditing();
  };

  handleChangeStartDate = startDate => {
    this.setState({ startDate }, this.setDateTimeHours);
  };

  handleChangeStartTime = startTime => {
    this.handleChangeTime('startTime', startTime);
  };

  handleChangeEndTime = endTime => {
    this.handleChangeTime('endTime', endTime);
  };

  handleChangeTime = (name, value) => {
    if (!value) {
      this.setState({
        hours: 0
      });
      this.props.calculateEstCost({ hours: 0 });
    }
    this.setState({ [name]: value }, this.setDateTimeHours);
  };

  setDateTimeHours = () => {
    let { startDate, startTime, endTime } = this.state;
    if (startDate && startTime && endTime) {
      const updatedData = resolveDateTime(startDate, startTime, endTime);
      this.setState({
        startTime: updatedData.startTime,
        endTime: updatedData.endTime,
        hours: updatedData.hours
      });
      this.props.calculateEstCost({ hours: updatedData.hours });
    }
  };

  handleChangeJobQtyWorkers = event => {
    const { value } = event.target;
    const quantity_of_workers = value === '' ? value : parseInt(value, 10);
    this.setState({ quantity_of_workers });
    this.props.calculateEstCost({ quantity_of_workers });
  };

  openAnotherLocationModal = () => {
    this.setState({ locationModalIsOpen: true });
  };

  closeAddLocationModal = () => {
    this.setState({ locationModalIsOpen: false });
  };

  addLocationToJob = locationData => {
    return this.props.addUserLocation(locationData).then(() => {
      const selectedLocation = {
        label: `${locationData.location_name}, ${locationData.address}`,
        value: locationData.id,
        data: locationData
      };
      this.handleSelectLocation(selectedLocation);
      this.closeAddLocationModal();
    });
  };

  handleSelectLocation = selectedLocation => {
    if (selectedLocation) {
      const { address, city, state, zip, coordinates } = selectedLocation.data;
      if (!address || !city || !state || !zip || !coordinates) { // this can happen only with invalid location data in db
        return this.props.UIShowAlert('Sorry, the address is invalid.');
      }
      this.props.getCostPayRateByData({ address_coordinates: coordinates, state });
    } else {
      this.props.updateJobRates({ job_cost_rate: 0, worker_pay_rate: 0 });
    }
    this.setState({ selectedLocation });
  };

  saveGeneralInfo = e => {
    e.preventDefault();
    const { selectedLocation } = this.state;
    const { worker_pay_rate, job_cost_rate } = this.props.jobRates;
    const fieldsToValidate = {
      ...this.state,
      address_coordinates: selectedLocation && selectedLocation.data.coordinates,
      worker_pay_rate,
      job_cost_rate
    };
    if (!this.validate(this.validationRules, fieldsToValidate)) {
      const { address_coordinates, worker_pay_rate, job_cost_rate } = this.validationErrors;
      if (!address_coordinates && (worker_pay_rate || job_cost_rate)) {
        this.props.UIShowAlert(alertMessages.costPayRateIssue);
      }
      return;
    }
    this.startLoading();
    const { editJob, getEventsList } = this.props;
    const { startTime, endTime, hours, quantity_of_workers } = this.state;
    const { address, state, city, zip, coordinates, location_name, job_location_type_id } = selectedLocation.data;
    const payload = {
      start_date: moment.utc(startTime).format(),
      end_date: moment.utc(endTime).format(),
      hours,
      quantity_of_workers,
      address,
      state,
      city,
      zip,
      address_coordinates: coordinates,
      location_name,
      job_location_type_id,
      job_cost_rate: getCentPrice(job_cost_rate),
      worker_pay_rate: getCentPrice(worker_pay_rate)
    };
    editJob(payload).then(() => {
      this.stopEditing();
      getEventsList();
      this.stopLoading();
    });
  };

  render () {
    const { jobDetails, companyLocations, locationsList, showEditIcons } = this.props;
    const { transactions, status, time_zone_id, event_id, event, address, state, city, zip, location_name,
      job_location_type_id } = jobDetails;
    const { isLoading, isEditing, startDate, startTime, endTime, hours, quantity_of_workers, selectedLocation,
      locationModalIsOpen } = this.state;
    const jobStatusIsSaved = isJobSaved(status);
    const todayInJobTimeZone = getNowInTimeZone(time_zone_id);
    const eventStartedOrPast = todayInJobTimeZone.startOf('day').isSameOrAfter(event.start_date && event.start_date.clone().startOf('day'));
    const jobMinDate = event_id && !eventStartedOrPast ? useTimezoneForDate(event.start_date, time_zone_id) : todayInJobTimeZone;
    const jobMaxDate = event_id && useTimezoneForDate(event.end_date, time_zone_id);
    const errors = this.validationErrors;
    const isEditingWithSavedJobStatus = isEditing && jobStatusIsSaved;
    const locationOptions = companyLocations.map(location => ({
      label: `${location.location_name}, ${location.address}`,
      value: location.id,
      data: location
    }));
    let jobLocationTypeLabel = '';
    const jobLocationTypeData = locationsList.find(location => location.id === job_location_type_id);
    if (jobLocationTypeData) {
      jobLocationTypeLabel = jobLocationTypeData.label_en;
    }
    return (
      <section>
        <form onSubmit={this.saveGeneralInfo}>
          <div className='d-flex justify-content-between'>
            <h4 className='job-details-section-header'>General Information</h4>
            { showEditIcons && !isLoading && (
              <ActionsGroup
                isEditing={isEditing}
                enterEditMode={this.startEditing}
                exitEditMode={this.cancelEditing}
              />
            )}
          </div>
          { isLoading ? <div className='loader-wrapper'><Loader /></div> : (
            <Fragment>
              { transactions && <Transactions transactions={transactions} /> }
              <div className='section-row'>
                <span className='icon-wrapper'><FontAwesomeIcon icon={faCalendarAlt} /></span>
                { isEditingWithSavedJobStatus ? (
                  <div className='stretch-width-wrapper'>
                    <DatePicker
                      className='input'
                      placeholderText='Click to select a date'
                      selected={startDate}
                      onChange={this.handleChangeStartDate}
                      dateFormat={dayDateFormat}
                      minDate={jobMinDate}
                      maxDate={jobMaxDate}
                      dropdownMode='select'
                    />
                    <ErrorMessage errorText={errors.startDate} />
                  </div>
                ) : (
                  <p>{startDate.format(dayDateFormat)}</p>
                )}
              </div>
              <div className='section-row'>
                <span className='icon-wrapper'><FontAwesomeIcon icon={faClock} /></span>
                <div className='time-info-wrapper'>
                  {isEditing ? (
                    <React.Fragment>
                      {shouldHoldStartTime(status) ? (
                        <div className='mr-1 d-flex'>
                          <span>{moment(startTime).format(timeFormatShort)}</span>
                          <span className='ml-1'>-</span>
                        </div>
                      ) : (
                        <div>
                          <TimePicker
                            showSecond={false}
                            placeholder='Start Time'
                            onChange={this.handleChangeStartTime}
                            format={timeFormatUi}
                            use12Hours
                            minuteStep={15}
                            value={startTime}
                            defaultOpenValue={moment().startOf('day')}
                          />
                          <ErrorMessage errorText={errors.startTime} />
                        </div>
                      )}
                      <div>
                        <TimePicker
                          showSecond={false}
                          placeholder='End Time'
                          onChange={this.handleChangeEndTime}
                          format={timeFormatUi}
                          use12Hours
                          minuteStep={15}
                          value={endTime}
                          defaultOpenValue={moment().startOf('day')}
                        />
                        <ErrorMessage errorText={errors.endTime} />
                      </div>
                      {!!hours && <p className='pt-1'>{getJobTimeDurationUi(hours)}</p>}
                    </React.Fragment>
                  ) : getTimeInfoUi(startTime, endTime, hours)}
                  { !errors.startTime && !errors.endTime && <ErrorMessage className='w-100' errorText={errors.hours} /> }
                </div>
              </div>
              <div className='section-row'>
                <span className='icon-wrapper'><FontAwesomeIcon icon={faUsers} /></span>
                <div className='flex-wrapper'>
                  {isEditing ? (
                    <div>
                      Quantity of workers:
                      <input
                        className=''
                        type='number'
                        min='1'
                        name='quantity_of_workers'
                        value={quantity_of_workers}
                        onChange={this.handleChangeJobQtyWorkers}
                      />
                      <ErrorMessage errorText={errors.quantity_of_workers} />
                    </div>
                  ) : `Quantity of workers: ${quantity_of_workers}`}
                </div>
              </div>
              <div className='section-row'>
                <span className='icon-wrapper'><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <div className='address-info-wrapper'>
                  { isEditingWithSavedJobStatus ? (
                    <React.Fragment>
                      <Select
                        onChange={this.handleSelectLocation}
                        options={locationOptions}
                        placeholder='Choose'
                        value={selectedLocation}
                      />
                      <ErrorMessage errorText={errors.address_coordinates} />
                      <p className='mt-2'>
                        Choose from list or
                        <button
                          type='button'
                          className='additional-link non-button'
                          onClick={this.openAnotherLocationModal}>
                          Add Another Location
                        </button>
                      </p>
                    </React.Fragment>
                  ) : (
                    <Fragment>
                      <p className='mb-2'>Address: { address }</p>
                      <p className='mb-2'>{ city }, { state }, { zip }</p>
                      <p className='mb-2'>Location Name: { location_name }</p>
                      <p className='mb-2'>Location Type: { jobLocationTypeLabel }</p>
                    </Fragment>
                  )}
                </div>
              </div>
            </Fragment>
          )}
        </form>
        <AddLocationModal
          isOpen={locationModalIsOpen}
          closeModal={this.closeAddLocationModal}
          locationsList={locationsList}
          addLocationToJob={this.addLocationToJob}
        />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/jobDetails/JobDetailsTab/GeneralInfo.js